import { Message } from "primereact/message";
import * as React from "react";
import { FormEvent, useState } from "react";
import { useGetAuthModeQuery } from "../../../redux/services/auth";
import { useRegisterMutation } from "../../../redux/services/onboarding";
import AuthActionButton from "../components/auth-action-button";
import AuthFormInput from "../components/auth-form-input";
import AuthFormTitle from "../components/auth-form-title";
import AuthLink from "../components/auth-link";
import AuthSuccessMessage from "../components/auth-success-message";
import GoogleAuthButton from "../components/google-auth-button";

const SignUpProspect: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "idle" | "submitting" | "verification_sent" | "error"
  >("idle");
  const [register] = useRegisterMutation();
  const { data: authMode } = useGetAuthModeQuery();
  const isCognitoEnabled = authMode?.data?.isCognitoEnabled === true;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "submitting") return; // Block submitting if already in progress
    setStatus("submitting");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;

    try {
      await register({ email }).unwrap();
      setStatus("verification_sent");
    } catch (error: any) {
      if (error?.data?.code === "USER_ALREADY_EXISTS") {
        setStatus("error");
        setError("A user with this email address already exists");
        return;
      }

      if (error?.data?.code === "COMPANY_ALREADY_EXISTS") {
        setStatus("error");
        setError(
          "Your company/organization has already been registered.\nPlease contact your admin to receive your invitation."
        );
        return;
      }

      setStatus("error");
      setError(
        error?.data?.message ||
          "An unexpected error occurred. Please try again."
      );
    }
  };

  if (status === "verification_sent") {
    return (
      <div className="mb-3">
        <AuthSuccessMessage
          title="We've sent a verification email to the provided address"
          message="Please check your inbox for the link to verify your email and complete your company registration"
          helpText="If you don't see it, please check your junk folder"
        />
      </div>
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-column gap-3">
        <AuthFormTitle label="Register your account" />
        <div className="flex flex-column">
          <AuthFormInput
            id="email"
            name="email"
            type="email"
            label="Email"
            placeholder="Enter your email"
            required
            helpText="This will serve as the primary admin account for your company"
          />

          {error && (
            <small
              className="p-error block mt-2"
              style={{ whiteSpace: "pre-line" }}
            >
              <i className="pi pi-exclamation-circle mr-2" />
              {error}
            </small>
          )}
        </div>

        <AuthActionButton
          label="Continue"
          disabled={status === "submitting"}
          loading={status === "submitting"}
        />

        {isCognitoEnabled && (
          <>
            <div className="flex align-items-center my-2">
              <div className="flex-1 border-top-1 border-300" />
              <span className="px-3 text-500 text-sm">or</span>
              <div className="flex-1 border-top-1 border-300" />
            </div>

            <GoogleAuthButton
              mode="signup"
              nextPath="/news-agents/new"
              disabled={status === "submitting"}
              className="w-full"
            />
          </>
        )}

        <AuthLink
          text="Already have an account? "
          linkText="Sign in"
          to="/login"
          className="text-left mt-2"
        />
      </form>
    </div>
  );
};

export default SignUpProspect;
